import { Box, GlobalStyles, styled } from '@mui/material';
import { FC } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { useMountEffect } from 'react-utils';
import MaintenanceShield from '../../maintenance/containers/MaintenanceShield';
import TelemetryManager from '../../telemetry';
import ErrorBoundaryContainer from '../containers/ErrorBoundaryContainer';
import AppDrawer from './AppDrawer';
import AppHeader from './AppHeader';
import AppLoader from './AppLoader';
import ClearErrorOnRouteChange from './error-handling/ClearErrorOnRouteChange';
import LayoutSwitcher from './LayoutSwitcher';
import MobileSheild from './MobileSheild';
import AppNavigationProvider from './providers/AppNavigationProvider';
import ForceLogoutProvider from './providers/ForceLogoutProvider';
import HubSpotTrackingProvider from './providers/HubSpotTrackingProvider';
import SnackbarProvider from './SnackbarProvider';
import RequiresAuthentication from './user/RequiresAuthentication';

const App: FC<React.PropsWithChildren<unknown>> = () => {
  useMountEffect(() => {
    TelemetryManager.init();
  });
  return (
    <>
      <GlobalStyles
        styles={{
          'html,body, #root': { height: '100%', fontWeight: 300 },
        }}
      />
      <MobileSheild>
        <MaintenanceShield>
          <SnackbarProvider>
            <ErrorBoundaryContainer>
              <BrowserRouter>
                <AppNavigationProvider />
                <HubSpotTrackingProvider />
                <ForceLogoutProvider />
                <AppLoader>
                  <Box display='flex' height='100%'>
                    <AppHeader />
                    <RequiresAuthentication>
                      <AppDrawer />
                    </RequiresAuthentication>
                    <Box
                      component='main'
                      flexGrow={1}
                      display='flex'
                      overflow='hidden'
                      flexDirection='column'>
                      <DrawerHeader />
                      <LayoutSwitcher />
                    </Box>
                  </Box>
                </AppLoader>
                <ClearErrorOnRouteChange />
              </BrowserRouter>
            </ErrorBoundaryContainer>
          </SnackbarProvider>
        </MaintenanceShield>
      </MobileSheild>
    </>
  );
};

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

export default App;
